// src/app/component/wizard-modal/V2/wizard-modal.component.ts
import {
  Component, Input, Output, EventEmitter, OnInit, inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder, ReactiveFormsModule, Validators
} from '@angular/forms';
import { of, catchError } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { WizardDefaultsDto, WizardService } from '../../../core/services/wizard.service';

type AnyObj = { [k: string]: any };

@Component({
  selector: 'app-wizard-modal-v2',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './wizard-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./wizard-modal.component.scss'],
})
export class WizardModalComponent implements OnInit {
  @Input({ required: true }) establishmentId!: string;
  @Input() defaults?: WizardDefaultsDto | null;

  @Output() close = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private api = inject(WizardService);

  loading = true;
  submitting = false;

  /** Étape courante du wizard */
  step = 0;

  /** 🔑 version / wizardId actifs pour isoler les brouillons V2 */
  private defaultsVersion: number | null = null;
  private defaultsWizardId: string | null = null;

  form = this.fb.group({
    opening_hours: this.fb.control<string | null>(null),
    has_triage: this.fb.control<boolean | null>(null),
    nurses: this.fb.control<number | null>(null, { validators: [Validators.min(0)] }),
    doctors: this.fb.control<number | null>(null, { validators: [Validators.min(0)] }),
    priority_rules: this.fb.control<any | null>(null),
  });

  ngOnInit(): void {
    if (this.defaults) {
      this.applyDefaults(this.defaults);
      this.loading = false;
      this.tryLoadDraft();
      return;
    }
    this.api.getDefaults(this.establishmentId).subscribe({
      next: d => { this.applyDefaults(d); },
      error: () => {},
      complete: () => { this.loading = false; this.tryLoadDraft(); },
    });
  }

  // ======================
  // Navigation
  // ======================
  start(): void {
    this.step = 1;
    this.saveDraft(1); // autosave
  }

  next(): void {
    if (this.step < 3) {
      this.step++;
      this.saveDraft(this.step); // autosave
    }
  }

  prev(): void {
    if (this.step > 1) {
      this.step--;
      this.saveDraft(this.step); // autosave
    }
  }

  onBackdrop(): void {
    this.onCancel();
  }

  // ======================
  // Chargement des defaults/drafts
  // ======================
  private tryLoadDraft(): void {
    const ver = this.defaultsVersion;
    // 1) Serveur
    this.api.loadEstablishmentProgress(this.establishmentId, ver).subscribe(
        saved => {
          const data: AnyObj | null = (saved && (saved as AnyObj)['data']) || null;
          const metaStep = Number((saved as AnyObj)?.['meta']?.['step']);
          if (data) this.applyPayloadData(data);
          if (Number.isFinite(metaStep) && metaStep >= 0) this.step = metaStep;

          // Si rien côté serveur → fallback local
          if (!data) this.tryLoadDraftLocal();
        },
        _err => {
          // 2) Fallback local si erreur API
          this.tryLoadDraftLocal();
        }
    );
  }

  private tryLoadDraftLocal(): void {
    const local = this.api.localLoad(this.establishmentId, this.defaultsVersion, this.defaultsWizardId);
    const data: AnyObj | null = (local && (local as AnyObj)['data']) || null;
    const metaStep = Number((local as AnyObj)?.['meta']?.['step']);
    if (data) this.applyPayloadData(data);
    if (Number.isFinite(metaStep) && metaStep >= 0) this.step = metaStep;
  }

  private applyDefaults(d: WizardDefaultsDto): void {
    // 🔑 mémoriser pour la clé de brouillon
    this.defaultsVersion = d?.version ?? null;
    this.defaultsWizardId = d?.wizardId ?? null;

    const anyD = d as AnyObj;
    const payload: AnyObj = (anyD && (anyD['payloadDefault'] ?? anyD['payload'])) ?? {};

    const cfg: AnyObj = (payload && payload['config']) ?? {};
    const resources: AnyObj = (payload && payload['resources']) ?? {};
    const rules: AnyObj = (payload && payload['rules']) ?? {};

    this.form.patchValue({
      opening_hours: cfg['opening_hours'] ?? '08:00-18:00',
      has_triage: !!cfg['has_triage'],
      nurses: resources['nurses'] ?? 0,
      doctors: resources['doctors'] ?? 1,
      priority_rules: rules['priority'] ?? null,
    }, { emitEvent: false });
  }

  private applyPayloadData(payload: AnyObj): void {
    const cfg: AnyObj = (payload && payload['config']) ?? {};
    const resources: AnyObj = (payload && payload['resources']) ?? {};
    const rules: AnyObj = (payload && payload['rules']) ?? {};

    this.form.patchValue({
      opening_hours: cfg['opening_hours'] ?? null,
      has_triage: cfg['has_triage'] ?? null,
      nurses: resources['nurses'] ?? null,
      doctors: resources['doctors'] ?? null,
      priority_rules: rules['priority'] ?? null,
    }, { emitEvent: false });
  }

  // ======================
  // Sauvegarde réseau + fallback local
  // ======================
  private payloadFromForm(): AnyObj {
    const v = this.form.getRawValue();
    return {
      config: {
        opening_hours: v.opening_hours ?? '08:00-18:00',
        has_triage: !!v.has_triage,
      },
      resources: {
        nurses: v.nurses ?? 0,
        doctors: v.doctors ?? 1,
      },
      rules: {
        priority: v.priority_rules ?? null,
      },
    };
  }

  private saveDraft(stepOverride?: number): void {
    const metaStep = stepOverride ?? this.step;
    const body = { meta: { step: metaStep }, data: this.payloadFromForm() };

    this.api.saveEstablishmentProgress(this.establishmentId, body, this.defaultsVersion)
        .pipe(
            catchError((_err) => {
              // Fallback local par (estId, wizardId, version)
              this.api.localSave(this.establishmentId, this.defaultsVersion, body, this.defaultsWizardId);
              return of(void 0);
            })
        )
        .subscribe();
  }

  // ======================
  // Submit / Cancel
  // ======================
  onFinish(): void {
    if (this.form.invalid) return;
    this.submitting = true;

    const draft = { meta: { step: 4 }, data: this.payloadFromForm() };

    this.api.saveEstablishmentProgress(this.establishmentId, draft, this.defaultsVersion)
        .pipe(
            catchError((_err) => {
              // fallback local si l’endpoint n’existe pas encore
              this.api.localSave(this.establishmentId, this.defaultsVersion, draft, this.defaultsWizardId);
              return of(void 0);
            })
        )
        .subscribe(() => {
          this.api.completeEstablishment(this.establishmentId, this.defaultsVersion).subscribe({
            complete: () => {
              this.submitting = false;
              this.completed.emit();
              this.close.emit();
            }
          });
        });
  }

  onCancel(): void {
    const draft = { meta: { step: this.step }, data: this.payloadFromForm() };
    this.api.saveEstablishmentProgress(this.establishmentId, draft, this.defaultsVersion)
        .pipe(
            catchError((_err) => {
              this.api.localSave(this.establishmentId, this.defaultsVersion, draft, this.defaultsWizardId);
              return of(void 0);
            })
        )
        .subscribe(() => this.close.emit());
  }
}
